import { z } from 'zod';
import { isoDaySchema } from './dates';
import {
  KPI_AGGREGATIONS,
  type KpiAggregationName,
  type KpiDirectionName,
  type KpiPeriodicityName,
} from './kpis';
import { accumulate, consolidateUnits, kpiPerformance, trafficLight, type TrafficRule } from './performance';
import { TRAFFIC_LIGHTS, type TrafficLight } from './status';

/** Origem do dado realizado (RN-19). */
export const KPI_RESULT_SOURCES = ['MANUAL', 'IMPORTED', 'INTEGRATION', 'CALCULATED'] as const;
export type KpiResultSourceName = (typeof KPI_RESULT_SOURCES)[number];
export const KPI_RESULT_SOURCE_LABELS: Record<KpiResultSourceName, string> = {
  MANUAL: 'Lançamento manual',
  IMPORTED: 'Importação',
  INTEGRATION: 'Integração',
  CALCULATED: 'Cálculo por fórmula',
};

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const DAY = 86_400_000;

/** Dia ISO (AAAA-MM-DD) ↔ data UTC. */
const toDate = (iso: string) => new Date(`${iso}T00:00:00Z`);
const toIso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (iso: string, days: number) => toIso(new Date(toDate(iso).getTime() + days * DAY));
const lastDayOfMonth = (year: number, month: number) => toIso(new Date(Date.UTC(year, month, 0)));
const pad = (n: number) => String(n).padStart(2, '0');
/** Seis casas, como no banco (soma de decimais não deixa resíduo de ponto flutuante). */
const r6 = (v: number | null) => (v === null ? null : Math.round(v * 1e6) / 1e6);
const brDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

/** O que o cálculo do período precisa saber do KPI. */
export type ResultPeriodKpi = {
  periodicity: KpiPeriodicityName;
  /** Início da vigência (AAAA-MM-DD): conta os intervalos da periodicidade personalizada. */
  validFrom: string;
  customPeriodDays: number | null;
};

/**
 * Período da periodicidade do KPI que contém a data (DEC-029): o dia (diária), a semana a partir da
 * segunda-feira, o mês, o trimestre, o semestre, o ano ou o intervalo de N dias contado da vigência.
 */
export function resultPeriod(kpi: ResultPeriodKpi, day: string): { start: string; end: string } {
  const d = toDate(day);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  switch (kpi.periodicity) {
    case 'DAILY':
      return { start: day, end: day };
    case 'WEEKLY': {
      const weekday = (d.getUTCDay() + 6) % 7; // segunda = 0
      const start = addDays(day, -weekday);
      return { start, end: addDays(start, 6) };
    }
    case 'MONTHLY':
      return { start: `${y}-${pad(m)}-01`, end: lastDayOfMonth(y, m) };
    case 'QUARTERLY': {
      const first = Math.floor((m - 1) / 3) * 3 + 1;
      return { start: `${y}-${pad(first)}-01`, end: lastDayOfMonth(y, first + 2) };
    }
    case 'SEMIANNUAL': {
      const first = m <= 6 ? 1 : 7;
      return { start: `${y}-${pad(first)}-01`, end: lastDayOfMonth(y, first + 5) };
    }
    case 'ANNUAL':
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    case 'CUSTOM': {
      const size = Math.max(1, kpi.customPeriodDays ?? 1);
      const offset = Math.floor((d.getTime() - toDate(kpi.validFrom).getTime()) / DAY);
      const start = addDays(kpi.validFrom, Math.floor(offset / size) * size);
      return { start, end: addDays(start, size - 1) };
    }
  }
}

/** Rótulo do período para as telas e e-mails. */
export function resultPeriodLabel(kpi: ResultPeriodKpi, start: string): string {
  const { end } = resultPeriod(kpi, start);
  const y = start.slice(0, 4);
  const m = Number(start.slice(5, 7));
  switch (kpi.periodicity) {
    case 'DAILY':
      return brDate(start);
    case 'WEEKLY':
      return `Semana de ${brDate(start)}`;
    case 'MONTHLY':
      return `${MONTHS[m - 1]}/${y}`;
    case 'QUARTERLY':
      return `${Math.floor((m - 1) / 3) + 1}º tri/${y}`;
    case 'SEMIANNUAL':
      return `${m <= 6 ? 1 : 2}º sem/${y}`;
    case 'ANNUAL':
      return y;
    case 'CUSTOM':
      return `${brDate(start)} a ${brDate(end)}`;
  }
}

/**
 * Rótulo de um período já gravado (início e fim), sem depender da periodicidade atual do KPI: a trilha de
 * auditoria mostra o período do lançamento mesmo que o KPI mude de periodicidade depois (DEC-039).
 */
export function storedPeriodLabel(start: string, end: string): string {
  const y = Number(start.slice(0, 4));
  const m = Number(start.slice(5, 7));
  const firstOfMonth = start.slice(8, 10) === '01';
  const endsMonth = (months: number) =>
    firstOfMonth && end.slice(0, 4) === start.slice(0, 4) && end === lastDayOfMonth(y, m + months - 1);
  if (start === end) return brDate(start);
  if (endsMonth(1)) return `${MONTHS[m - 1]}/${y}`;
  if (endsMonth(3) && (m - 1) % 3 === 0) return `${(m - 1) / 3 + 1}º tri/${y}`;
  if (endsMonth(6) && (m === 1 || m === 7)) return `${m === 1 ? 1 : 2}º sem/${y}`;
  if (endsMonth(12) && m === 1) return `Ano ${y}`;
  if (addDays(start, 6) === end && (toDate(start).getUTCDay() + 6) % 7 === 0)
    return `Semana de ${brDate(start)}`;
  return `${brDate(start)} a ${brDate(end)}`;
}

/** Mês em que o período fecha (é nele que o resultado entra na série mensal — DEC-029). */
export const closingMonth = (end: string) => ({
  year: Number(end.slice(0, 4)),
  month: Number(end.slice(5, 7)),
});

/** Resultado vigente como entra no cálculo. */
export type SeriesResult = { periodEnd: string; businessUnitId: string | null; value: number | null };

/** Metas em vigor do ano, por tipo (as que existirem). */
export type SeriesTargets = {
  month: Map<number, number | null>;
  quarter: Map<number, number | null>;
  year: number | null;
  accumulated: Map<number, number | null>;
};

export type SeriesKpi = ResultPeriodKpi & {
  direction: KpiDirectionName;
  aggregation: KpiAggregationName;
  rangeLower: number | null;
  rangeUpper: number | null;
  performanceFormula: string | null;
};

export type MonthPoint = {
  month: number;
  actual: number | null;
  target: number | null;
  performance: number | null;
  light: TrafficLight;
  ytdActual: number | null;
  ytdTarget: number | null;
  ytdPerformance: number | null;
  ytdLight: TrafficLight;
};

/** Valores de cada unidade (na ordem recebida). */
function groupByUnit(results: SeriesResult[]): Map<string, (number | null)[]> {
  const out = new Map<string, (number | null)[]>();
  for (const r of results) {
    if (r.businessUnitId === null) continue;
    out.set(r.businessUnitId, [...(out.get(r.businessUnitId) ?? []), r.value]);
  }
  return out;
}

/**
 * Metas consolidadas (T-067, DEC-037): em cada período, a meta geral (inclusive a removida de propósito,
 * que fica sem meta); sem meta geral, a consolidação das metas das unidades pela mesma regra do realizado.
 */
export function consolidateTargets(
  general: SeriesTargets | undefined,
  byUnit: ReadonlyMap<string, SeriesTargets>,
  aggregation: KpiAggregationName,
  unitWeights?: ReadonlyMap<string, number>,
): SeriesTargets {
  const units = [...byUnit];
  const pick = (
    has: (t: SeriesTargets) => boolean,
    get: (t: SeriesTargets) => number | null | undefined,
  ): { present: boolean; value: number | null } => {
    if (general && has(general)) return { present: true, value: get(general) ?? null };
    const entries = units.filter(([, t]) => has(t)).map(([unitId, t]) => ({ unitId, value: get(t) ?? null }));
    if (!entries.length) return { present: false, value: null };
    return { present: true, value: r6(consolidateUnits(entries, aggregation, unitWeights)) };
  };
  const out: SeriesTargets = { month: new Map(), quarter: new Map(), year: null, accumulated: new Map() };
  for (let m = 1; m <= 12; m += 1) {
    const month = pick(
      (t) => t.month.has(m),
      (t) => t.month.get(m),
    );
    if (month.present) out.month.set(m, month.value);
    const acc = pick(
      (t) => t.accumulated.has(m),
      (t) => t.accumulated.get(m),
    );
    if (acc.present) out.accumulated.set(m, acc.value);
  }
  for (let q = 1; q <= 4; q += 1) {
    const quarter = pick(
      (t) => t.quarter.has(q),
      (t) => t.quarter.get(q),
    );
    if (quarter.present) out.quarter.set(q, quarter.value);
  }
  out.year = pick(
    (t) => t.year !== null,
    (t) => t.year,
  ).value;
  return out;
}

/** O período do KPI fecha neste mês? (meta do tipo do período só no mês em que ele fecha.) */
function closesIn(periodicity: KpiPeriodicityName, month: number): 'QUARTER' | 'YEAR' | 'SEMESTER' | null {
  if (periodicity === 'QUARTERLY') return month % 3 === 0 ? 'QUARTER' : null;
  if (periodicity === 'ANNUAL') return month === 12 ? 'YEAR' : null;
  if (periodicity === 'SEMIANNUAL') return month % 6 === 0 ? 'SEMESTER' : null;
  return null;
}

/**
 * Série mensal do KPI no ano (DEC-029): realizado do mês pela agregação do KPI (o geral, se houver; senão
 * o consolidado das unidades), meta em vigor do mês (a mensal; sem ela, no mês em que o período fecha, a
 * trimestral/anual ou a acumulada), performance e semáforo pelo motor da T-061, e o acumulado do ano.
 * Meses depois de `today` são futuros (azul) quando não têm realizado.
 */
export function kpiMonthSeries(input: {
  kpi: SeriesKpi;
  year: number;
  results: SeriesResult[];
  targets: SeriesTargets;
  rule: TrafficRule;
  cap: number;
  /** Hoje (AAAA-MM-DD), para marcar os meses futuros. */
  today: string;
  /** Peso de cada unidade na média ponderada (T-067); sem peso = 1. */
  unitWeights?: ReadonlyMap<string, number>;
}): MonthPoint[] {
  const { kpi, year, results, targets, rule, cap, today, unitWeights } = input;
  const points: MonthPoint[] = [];
  const actuals: (number | null)[] = [];
  const monthTargets: (number | null)[] = [];
  for (let month = 1; month <= 12; month += 1) {
    const inMonth = results
      .filter((r) => {
        const c = closingMonth(r.periodEnd);
        return c.year === year && c.month === month && r.value !== null;
      })
      .sort((a, b) => a.periodEnd.localeCompare(b.periodEnd));
    const general = inMonth.filter((r) => r.businessUnitId === null);
    // Com o geral, vale o geral (DEC-029). Sem ele, dois passos (T-067, DEC-037): cada unidade pela
    // agregação do KPI nos períodos do mês; depois a consolidação entre as unidades.
    const actual = r6(
      general.length
        ? accumulate(
            general.map((r) => r.value),
            kpi.aggregation,
          )
        : consolidateUnits(
            [...groupByUnit(inMonth)].map(([unitId, values]) => ({
              unitId,
              value: accumulate(values, kpi.aggregation),
            })),
            kpi.aggregation,
            unitWeights,
          ),
    );
    let target = targets.month.get(month) ?? null;
    if (target === null) {
      const closes = closesIn(kpi.periodicity, month);
      if (closes === 'QUARTER')
        target = targets.quarter.get(month / 3) ?? targets.accumulated.get(month) ?? null;
      else if (closes === 'YEAR') target = targets.year ?? targets.accumulated.get(12) ?? null;
      else if (closes === 'SEMESTER') target = targets.accumulated.get(month) ?? null;
    }
    actuals.push(actual);
    monthTargets.push(target);
    const future = actual === null && `${year}-${pad(month)}-01` > today;
    const perfInput = {
      direction: kpi.direction,
      actual,
      target,
      rangeLower: kpi.rangeLower,
      rangeUpper: kpi.rangeUpper,
      performanceFormula: kpi.performanceFormula,
    };
    const performance = kpiPerformance(perfInput, cap);
    // Acumulado do ano até o mês: agregação dos realizados contra a meta acumulada (ou a agregação das mensais).
    const ytdActual = r6(accumulate(actuals, kpi.aggregation));
    const ytdTarget = targets.accumulated.get(month) ?? r6(accumulate(monthTargets, kpi.aggregation));
    const ytdPerformance = kpiPerformance({ ...perfInput, actual: ytdActual, target: ytdTarget }, cap);
    points.push({
      month,
      actual,
      target,
      performance,
      light: trafficLight(performance, rule, kpi.direction, future),
      ytdActual,
      ytdTarget,
      ytdPerformance,
      ytdLight: trafficLight(ytdPerformance, rule, kpi.direction, future),
    });
  }
  return points;
}

// ---------------------------------------------------------------------------------------------
// Contrato da API

const ref = z.object({ id: z.string().uuid(), name: z.string() });

/** Versão vigente de um resultado. */
export const kpiResultSchema = z.object({
  id: z.string().uuid(),
  kpiId: z.string().uuid(),
  periodStart: z.string(),
  periodEnd: z.string(),
  periodLabel: z.string(),
  businessUnit: ref.nullable(),
  /** null = o resultado foi removido (há histórico). */
  value: z.number().nullable(),
  referenceDate: z.string(),
  justification: z.string().nullable(),
  evidence: z.object({ name: z.string(), url: z.string() }).nullable(),
  source: z.enum(KPI_RESULT_SOURCES),
  version: z.number().int(),
  createdByName: z.string(),
  createdAt: z.string(),
});
export type KpiResult = z.infer<typeof kpiResultSchema>;

/** Resultados do KPI no ano + a série mensal calculada. */
export const kpiResultListSchema = z.object({
  kpi: z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    unitLabel: z.string(),
    periodicity: z.string(),
    dataSource: z.string(),
    direction: z.string(),
  }),
  year: z.number().int(),
  items: z.array(kpiResultSchema),
  series: z.array(
    z.object({
      month: z.number().int(),
      actual: z.number().nullable(),
      target: z.number().nullable(),
      performance: z.number().nullable(),
      light: z.string(),
      ytdActual: z.number().nullable(),
      ytdTarget: z.number().nullable(),
      ytdPerformance: z.number().nullable(),
      ytdLight: z.string(),
    }),
  ),
});

export const kpiResultListQuerySchema = z.object({
  kpiId: z.string().uuid(),
  year: z.coerce.number().int().min(2000).max(2100),
});

/** Lançamento (célula): o período sai da data de referência e da periodicidade do KPI. */
export const kpiResultWriteSchema = z.object({
  kpiId: z.string().uuid(),
  referenceDate: isoDaySchema,
  businessUnitId: z.string().uuid().nullable().default(null),
  /** null = remover o resultado vigente do período (nova versão, com motivo). */
  value: z
    .number({ message: 'Informe o realizado em números.' })
    .finite()
    .refine((v) => Math.abs(v) < 1e14, 'Valor grande demais.')
    .nullable(),
  justification: z
    .string()
    .trim()
    .max(2000, 'A justificativa pode ter no máximo 2000 caracteres.')
    .nullable()
    .optional()
    .transform((v) => v || null),
});
export type KpiResultWrite = z.input<typeof kpiResultWriteSchema>;

export const kpiResultBatchSchema = z.object({
  items: z
    .array(kpiResultWriteSchema)
    .min(1, 'Nada para lançar.')
    .max(300, 'No máximo 300 lançamentos por vez.'),
  /** Motivo da correção: obrigatório ao mudar ou remover um resultado já lançado. */
  reason: z
    .string()
    .trim()
    .max(1000, 'O motivo pode ter no máximo 1000 caracteres.')
    .nullable()
    .optional()
    .transform((v) => v || null),
});
export type KpiResultBatch = z.input<typeof kpiResultBatchSchema>;

export const kpiResultBatchResultSchema = z.object({
  created: z.number().int(),
  corrected: z.number().int(),
  removed: z.number().int(),
  unchanged: z.number().int(),
  items: z.array(kpiResultSchema),
});

export const kpiResultHistorySchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      version: z.number().int(),
      value: z.number().nullable(),
      referenceDate: z.string(),
      justification: z.string().nullable(),
      reason: z.string().nullable(),
      evidence: z.object({ name: z.string(), url: z.string() }).nullable(),
      source: z.enum(KPI_RESULT_SOURCES),
      createdByName: z.string(),
      createdAt: z.string(),
      current: z.boolean(),
    }),
  ),
});
export const kpiResultHistoryQuerySchema = z.object({
  kpiId: z.string().uuid(),
  periodStart: isoDaySchema,
  businessUnitId: z.string().uuid().optional(),
});

/** Grade de lançamento do mês (T-064c): uma linha por KPI que a pessoa pode ver ou lançar. */
export const kpiResultGridQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
});
export const kpiResultGridRowSchema = z.object({
  kpi: z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    unitLabel: z.string(),
    periodicity: z.string(),
    /** Início da vigência e intervalo da periodicidade personalizada: o período de uma data (DEC-029). */
    validFrom: z.string(),
    customPeriodDays: z.number().int().nullable(),
    dataSource: z.string(),
    direction: z.string(),
    perspective: ref,
  }),
  /**
   * Acesso: AREA = o KPI é de uma área da pessoa (lança o geral e vê o cálculo); UNIT = só pela unidade
   * dela (lança o realizado da unidade, sem ver o consolidado).
   */
  access: z.enum(['AREA', 'UNIT']),
  /** Período do KPI que fecha no mês (null: diária, semanal e personalizada — lançadas pela data —, ou nenhum fecha). */
  period: z.object({ start: z.string(), end: z.string(), label: z.string() }).nullable(),
  /** Data de referência que a grade usa ao lançar (fim do mês, ou hoje no mês corrente). */
  referenceDate: z.string().nullable(),
  /** Resultado geral vigente do período (null = não lançado; sempre null para quem só lança pela unidade). */
  result: kpiResultSchema.nullable(),
  /** Resultados por unidade do período que a pessoa pode ver (todas na área; só as suas pela unidade). */
  unitResults: z.array(kpiResultSchema),
  /** Períodos distintos com realizado que fecham no mês (que a pessoa pode ver) — útil nas diárias e semanais. */
  entriesInMonth: z.number().int(),
  /** Cálculo do mês com o que já está lançado (null para quem só lança pela unidade). */
  month: z
    .object({
      actual: z.number().nullable(),
      target: z.number().nullable(),
      performance: z.number().nullable(),
      light: z.string(),
    })
    .nullable(),
  /** Pode lançar: KPI não calculado, mês dentro da vigência. */
  editable: z.boolean(),
  /** Motivo de não poder lançar (para a tela explicar). */
  blockedReason: z.string().nullable(),
});
export type KpiResultGridRow = z.infer<typeof kpiResultGridRowSchema>;
export const kpiResultGridSchema = z.object({
  year: z.number().int(),
  month: z.number().int(),
  /** O mês está fechado (T-066): lançar ou corrigir exige justificativa (RN-06). */
  closed: z.boolean(),
  rows: z.array(kpiResultGridRowSchema),
});

// ---------------------------------------------------------------------------------------------
// Consolidação por unidades e filiais (T-067, RF-53, DEC-037)

const light = z.enum(TRAFFIC_LIGHTS);
/** Mês de uma série: realizado, meta, performance e semáforo, no mês e no acumulado do ano. */
export const monthPointSchema = z.object({
  month: z.number().int(),
  actual: z.number().nullable(),
  target: z.number().nullable(),
  performance: z.number().nullable(),
  light,
  ytdActual: z.number().nullable(),
  ytdTarget: z.number().nullable(),
  ytdPerformance: z.number().nullable(),
  ytdLight: light,
});

/** Acesso ao KPI no comparativo: AREA vê todas as unidades e o consolidado; UNIT, só as suas unidades. */
export const KPI_UNIT_ACCESS = ['AREA', 'UNIT'] as const;

/** KPIs que a pessoa pode comparar por unidade (os da sua área, ou todos quando ela tem unidade). */
export const unitComparisonKpisSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      perspective: ref,
      access: z.enum(KPI_UNIT_ACCESS),
      /** Arquivado (só vem com `archived=include`: o histórico continua consultável — RN-05). */
      archived: z.boolean(),
    }),
  ),
});
export const unitComparisonKpisQuerySchema = z.object({ archived: z.enum(['include']).optional() });

export const unitComparisonQuerySchema = z.object({
  kpiId: z.string().uuid(),
  year: z.coerce.number().int().min(2000).max(2100),
});

/** Comparativo de um KPI por unidade no ano: a série de cada unidade e a consolidada. */
export const unitComparisonSchema = z.object({
  kpi: z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    unitLabel: z.string(),
    aggregation: z.enum(KPI_AGGREGATIONS),
    direction: z.string(),
  }),
  year: z.number().int(),
  access: z.enum(KPI_UNIT_ACCESS),
  /** Consolidado da empresa (null para quem só vê pela unidade). */
  consolidated: z.array(monthPointSchema).nullable(),
  units: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      code: z.string().nullable(),
      /** Peso na média ponderada. */
      weight: z.number(),
      archived: z.boolean(),
      months: z.array(monthPointSchema),
    }),
  ),
});
export type UnitComparison = z.infer<typeof unitComparisonSchema>;
